// src/pages/DocumentActionsPanel.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Search,
  AlertTriangle,
  FileText,
  ShieldCheck,
  ChevronRight,
  ArrowLeft,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { slugify } from '../utils/slugify';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { useDispatch, useSelector } from 'react-redux';
import { clearDocument } from '../../redux/slices/documentSlice';
import { analyzeContract, getContractReport } from '../../api/api';
import { storeContractReport } from '../../redux/slices/reportSlice';
import ScaleLogo from '../../assets/scale-balance.svg';
import { fetchChatSessions } from '../../redux/slices/chatSessionsSlice';
import toast from 'react-hot-toast';
import { handleApiError } from '../../helpers/handleApiError';
import PlanLimitReachedModal from '../../views/PlanLimitReachedModal';
import UpgradeModal from '../usage/UpgradeModal';

const actions = [
  {
    icon: <Search className="h-5 w-5 text-black" />,
    title: 'Full Contract Analysis',
    analysisType: 'full',
  },
  {
    icon: <AlertTriangle className="h-5 w-5 text-black" />,
    title: 'Risk Analysis',
    analysisType: 'risk',
  },
  {
    icon: <FileText className="h-5 w-5 text-black" />,
    title: 'Extract Key Clauses',
    analysisType: 'clauses',
  },
  {
    icon: <ShieldCheck className="h-5 w-5 text-black" />,
    title: 'Compliance Check',
    analysisType: 'compliance',
  },
  {
    icon: <Search className="h-5 w-5 text-black" />,
    title: 'Question & Answer',
    analysisType: 'qa',
  },
];

export default function DocumentActionsPanel({ isDarkMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const uid = user?.id;
  const reduxFileName = useSelector((state) => state.document.selectedFileName);
  const fileName =
    location.state?.fileName || reduxFileName || 'Unknown Document';
  const stats = useSelector((state) => state.userStatistics.stats);

  const remainingDocumentAnalysis =
    stats?.usage_month_to_date?.document_analysis?.value;
  const activeOrder = user?.subscription || null;

  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const formatFileName = (name, maxLength = 48) => {
    if (!name || name.length <= maxLength) return name;

    const extIndex = name.lastIndexOf('.');
    const ext = extIndex !== -1 ? name.slice(extIndex) : '';
    const base = extIndex !== -1 ? name.slice(0, extIndex) : name;

    const keep = Math.floor((maxLength - ext.length - 3) / 2);

    return `${base.slice(0, keep)}...${base.slice(-keep)}${ext}`;
  };

  // useEffect(() => {
  //   if (!location.state?.fileName && !reduxFileName) {
  //     dispatch(clearDocument());
  //     navigate('/app/document', { replace: true });
  //   }
  // }, [location.state, reduxFileName, dispatch, navigate]);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);

  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);
  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    setMessages((prev) => [...prev, userMessage]);
    const question = input;
    setInput('');
    setLoading(true);

    try {
      const res = await analyzeContract(uid, 'qa', fileName, question);

      let content = '';
      if (res?.results && typeof res.results === 'object') {
        content = Object.values(res.results)
          .filter((val) => typeof val === 'string')
          .join('\n\n');
      }
      if (!content.trim()) {
        content = '```json\n' + JSON.stringify(res, null, 2) + '\n```';
      }

      const botMessage = { role: 'bot', content };
      setMessages((prev) => [...prev, botMessage]);
      dispatch(fetchChatSessions());
      setLoading(false);

      try {
        const updatedReport = await getContractReport(uid, fileName);
        if (updatedReport) {
          dispatch(storeContractReport(updatedReport.report));
        }
      } catch (error) {
        handleApiError(error, 'Failed to fetch updated report');
      }
    } catch (err) {
      console.error(err);
      setMessages((prev) => [
        ...prev,
        { role: 'bot', content: '⚠️ Failed to get answer.' },
      ]);
      setLoading(false);
    }
  };

  return (
    <div
      className={`w-full overflow-y-auto px-6 py-2 ${
        isDarkMode ? 'bg-[#111827]' : 'bg-white'
      }`}
    >
      {/* Back button */}
      <div>
        <button
          onClick={() => {
            // dispatch(clearDocument());
            navigate('/app/document');
          }}
          className="group inline-flex items-center gap-2 px-4 py-2 rounded-md border transition-all duration-200 shadow-sm
               bg-brand text-white border-transparent hover:opacity-90"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
        </button>
      </div>

      {/* Title & Description */}
      <h2
        className={`text-2xl font-semibold mt-3 mb-1 truncate ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}
        title={fileName}
      >
        {formatFileName(fileName)}
      </h2>

      <p
        className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
      >
        Choose a Predefined Document Check or Ask Follow Up Questions.
      </p>

      {/* Action Buttons */}
      <div className="max-w-xl mx-auto mt-6 space-y-3 text-left">
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={() => {
              if (remainingDocumentAnalysis === 0) {
                setShowLimitModal(true);
                return;
              }
              if (activeOrder?.status === 'paused') {
                toast.error(
                  'Your subscription is paused. Please resume your subscription to continue.'
                );
                return;
              }

              navigate(`/app/document/action/${slugify(action.title)}`, {
                state: { fileName, analysisType: action.analysisType },
              });
            }}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition ${
              isDarkMode
                ? 'bg-gray-800 hover:bg-gray-700'
                : 'bg-brandLighter hover:bg-[#E8E8FC]'
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-md shadow-sm ${
                  isDarkMode
                    ? 'bg-brand'
                    : 'bg-brandLighter border-2 border-brandLight'
                }`}
              >
                {action.icon}
              </div>
              <span
                className={`font-medium text-base ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}
              >
                {action.title}
              </span>
            </div>
            <ChevronRight
              className={`w-5 h-5 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            />
          </button>
        ))}
      </div>

      {/* Messages */}
      <div className="mt-6 max-w-6xl mx-auto space-y-3 pr-1">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`p-3 rounded-lg text-sm shadow ${
              msg.role === 'user'
                ? isDarkMode
                  ? 'bg-indigo-900 text-indigo-200 text-right'
                  : 'bg-indigo-100 text-indigo-800 text-right'
                : isDarkMode
                ? 'bg-gray-700 text-gray-200 text-left'
                : 'bg-gray-100 text-gray-800 text-left'
            }`}
          >
            {msg.role === 'bot' ? (
              <ReactMarkdown
                rehypePlugins={[rehypeRaw]}
                components={{
                  strong: ({ children, ...props }) => (
                    <strong className="font-bold text-brand" {...props}>
                      {children}
                    </strong>
                  ),

                  h1: ({ children, ...props }) => (
                    <h1
                      className="text-2xl font-bold text-brand mb-2"
                      {...props}
                    >
                      {children}
                    </h1>
                  ),

                  h2: ({ children, ...props }) => (
                    <h2
                      className="text-xl font-semibold text-brand mb-2"
                      {...props}
                    >
                      {children}
                    </h2>
                  ),

                  h3: ({ children, ...props }) => (
                    <h3
                      className="text-lg font-medium text-brand mb-1"
                      {...props}
                    >
                      {children}
                    </h3>
                  ),

                  li: ({ children, ...props }) => (
                    <li className="mb-1" {...props}>
                      {children}
                    </li>
                  ),
                }}
              >
                {String(msg.content).replaceAll('\\n', '\n')}
              </ReactMarkdown>
            ) : (
              msg.content
            )}
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div
              className={`relative max-w-[85%]  px-3 py-1.5${
                isDarkMode ? 'bg-gray-700' : 'bg-white'
              }`}
            >
              {/* Top Row: Avatar + Text */}
              <div className="flex items-center mb-1">
                {/* Avatar/Icon */}
                <div className="p-1 rounded-full bg-brandLighter shadow-sm w-6 h-6 flex items-center justify-center">
                  <img src={ScaleLogo} alt="Scale" className="w-4 h-4" />
                </div>

                {/* Thinking text + animation */}
                <div className="flex items-center space-x-2 ml-2">
                  <span className="text-sm text-gray-600">Thinking</span>
                  <div className="flex space-x-1.5 py-0.5">
                    <div className="h-1.5 w-1.5 bg-brand rounded-full animate-bounce"></div>
                    <div
                      className="h-1.5 w-1.5 bg-brand rounded-full animate-bounce"
                      style={{ animationDelay: '0.2s' }}
                    ></div>
                    <div
                      className="h-1.5 w-1.5 bg-brand rounded-full animate-bounce"
                      style={{ animationDelay: '0.4s' }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      {/* <div className="pt-6 px-4">
        <div className="w-full max-w-2xl mx-auto">
          <div
            className={`flex items-center border rounded-xl px-4 py-3 shadow-sm ${
              isDarkMode
                ? 'border-gray-700 bg-gray-800'
                : 'border-gray-200 bg-white'
            }`}
          >
            <input
              type="text"
              value={input}
              disabled={loading}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Ask a Document Question"
              className={`flex-grow text-sm outline-none placeholder-gray-400 ${
                isDarkMode ? 'text-gray-100 bg-transparent' : 'text-gray-800'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className={`ml-3 p-2 rounded-md transition ${
                isDarkMode
                  ? 'bg-brand text-white hover:bg-brand'
                  : 'bg-brand text-white hover:bg-brandDark'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div> */}

      <PlanLimitReachedModal
        open={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        activeOrder={activeOrder}
        onUpgrade={() => {
          setShowLimitModal(false);
          setShowUpgradeModal(true);
        }}
      />

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
}
